import React, { useMemo, useState } from 'react'
import { FaSearch } from 'react-icons/fa'
import { Bar, ChipSelect, Empty, Pill, Seg } from './fieldUi'
import { useField } from './fieldContext'
import { storeInfo } from '../store'
import { DAY_SHORT, fmtShort, money, weekday } from '../FieldOpsKit'
import { VISIT_LABEL } from './stops'

/** My stores (salesperson): every store of the salesperson, searchable, with goal, last visit and credit status. */
export default function StoresScreen() {
    const f = useField()
    const { state, nav, actor, stops } = f
    const [q, setQ] = useState('')
    const [filter, setFilter] = useState('all')
    const [sort, setSort] = useState('priority')
    const [limit, setLimit] = useState(30)

    const rows = useMemo(() => state.sales.stores.filter((s) => s.rep === actor.rep).map((s) => ({ s: s, info: storeInfo(state.sales, s.id, f.vIdx, f.oIdx) })), [state.sales, actor.rep]) // eslint-disable-line react-hooks/exhaustive-deps
    const inRoute = {}
    stops.forEach((x) => { if (x.kind === 'visit') inRoute[x.store.id] = true })

    const counts = {
        behind: rows.filter((r) => r.info.state.key === 'behind').length, met: rows.filter((r) => r.info.state.key === 'met').length,
        stale: rows.filter((r) => r.info.daysSince === null || r.info.daysSince >= 14).length, hold: rows.filter((r) => r.s.hold).length,
    }
    const list = rows.filter((r) => {
        if (q.trim() && (r.s.name + ' ' + r.s.city + ' ' + r.s.address).toLowerCase().indexOf(q.trim().toLowerCase()) < 0) return false
        if (filter === 'today') return !!inRoute[r.s.id]
        if (filter === 'behind') return r.info.state.key === 'behind'
        if (filter === 'stale') return r.info.daysSince === null || r.info.daysSince >= 14
        if (filter === 'hold') return r.s.hold
        return true
    }).sort((a, b) => {
        if (sort === 'name') return a.s.name.localeCompare(b.s.name)
        if (sort === 'visit') return (b.info.daysSince === null ? 9999 : b.info.daysSince) - (a.info.daysSince === null ? 9999 : a.info.daysSince)
        const pa = (a.info.state.key === 'behind' ? 100 : 0) + (a.info.missedThisMonth * 10) + (a.info.daysSince || 0) / 3
        const pb = (b.info.state.key === 'behind' ? 100 : 0) + (b.info.missedThisMonth * 10) + (b.info.daysSince || 0) / 3
        return pb - pa
    })

    return (
        <div className="fo-stack">
            <div className="fo-grid3">
                <div className="fo-stat"><span>My stores</span><strong>{rows.length}</strong></div>
                <div className="fo-stat"><span>Goal met</span><strong style={{ color: '#059669' }}>{counts.met}</strong></div>
                <div className="fo-stat"><span>Behind goal</span><strong style={{ color: '#D97706' }}>{counts.behind}</strong></div>
            </div>
            <div className="fo-searchbox"><FaSearch /><input className="fo-input" placeholder="Search by name, city or address" value={q} onChange={(e) => { setQ(e.target.value); setLimit(30) }} aria-label="Search stores" /></div>
            <div className="fo-catscroll"><ChipSelect value={filter === 'all' ? '' : filter} onChange={(v) => { setFilter(v || 'all'); setLimit(30) }} options={[{ key: 'today', label: 'In today’s route (' + Object.keys(inRoute).length + ')' }, { key: 'behind', label: 'Behind goal (' + counts.behind + ')' }, { key: 'stale', label: '14+ days without visit (' + counts.stale + ')' }, { key: 'hold', label: 'Credit hold (' + counts.hold + ')' }]} /></div>
            <Seg full value={sort} onChange={setSort} items={[{ key: 'priority', label: 'Priority' }, { key: 'visit', label: 'Longest unvisited' }, { key: 'name', label: 'Name' }]} />
            {list.length === 0 ? <Empty title="No stores match" text="Try another search or filter." /> : (
                <div className="fo-stack" style={{ gap: 10 }}>
                    {list.slice(0, limit).map((r) => (
                        <button key={r.s.id} type="button" className="fo-storerow" onClick={() => nav.push({ type: 'store', storeId: r.s.id })}>
                            <div className="fo-storerow__top">
                                <div><strong>{r.s.name}</strong><span>{r.s.city} · {r.s.route} · {DAY_SHORT[r.s.day] || DAY_SHORT[weekday('2026-11-02')]}</span></div>
                                <div className="fo-storerow__pills">{r.s.hold ? <Pill tone="red">Credit hold</Pill> : null}{inRoute[r.s.id] ? <Pill tone="blue">Today</Pill> : null}<Pill tone={r.info.state.tone}>{r.info.state.label}</Pill></div>
                            </div>
                            {r.info.goal ? <div className="fo-storerow__goal"><Bar value={r.info.sold} max={r.info.goal} tone={r.info.state.key === 'behind' ? 'amber' : r.info.state.key === 'met' ? 'green' : 'blue'} /><span>{money(r.info.sold)} of {money(r.info.goal)}</span></div> : null}
                            <div className="fo-storerow__meta">Last visit: {r.info.lastVisit ? fmtShort(r.info.lastVisit.date) + ' · ' + (VISIT_LABEL[r.info.lastVisit.outcome] || '') : 'never'}{r.info.missedThisMonth > 0 ? ' · missed ' + r.info.missedThisMonth + '× this month' : ''}</div>
                        </button>
                    ))}
                    {list.length > limit ? <button type="button" className="fo-btn fo-btn--ghost fo-btn--block" onClick={() => setLimit(limit + 30)}>Show more ({list.length - limit} left)</button> : null}
                </div>
            )}
        </div>
    )
}
